import React, { useEffect, useMemo, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import PopupBase from '../../popup/base/PopupBase';
import CustomButton from '../buttons/CustomButton';
import { DataProviderInstance } from '../../api/DataProvider';
import { bandsFor, previewSentence, resolveRoomFor, toTimeKey } from './dayRooms';
import styles from './DayRoomPopup.module.css';

const DAY_START = '00:00';
const WEEKDAYS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
const MONTHS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

function formatDate(dayKey) {
  const [y, m, d] = dayKey.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return `${WEEKDAYS[date.getDay()]}, ${d} de ${MONTHS[m - 1]}`;
}

/** Horário sugerido para "a partir de": a hora atual quando o dia é hoje, senão meio-dia. */
function suggestedTime(dayKey) {
  const now = new Date();
  const [y, m, d] = dayKey.split('-').map(Number);
  const isToday = now.getFullYear() === y && now.getMonth() === m - 1 && now.getDate() === d;
  return isToday ? toTimeKey(now.getHours(), 0) : '12:00';
}

/**
 * Popup "sala do dia" de uma agenda: escolhe a sala (ou "sem sala"), o dia todo ou a
 * partir de um horário, mostra quantos agendamentos mudam e aplica.
 *
 * @param {Object} props.resource - { Id, Label, DefaultRoomId }
 * @param {string} props.date - YYYY-MM-DD
 * @param {Array} props.rooms - CompanyConfig.Rooms ({ Id, Label, Capacity })
 * @param {Array} props.assignments - faixas do dia (useDayRooms)
 * @param {Function} props.onClose
 * @param {Function} props.onApplied - ({ changed, kept, roomLabel }) depois de gravar
 */
function DayRoomPopup({ resource, date, rooms = [], assignments = [], onClose, onApplied }) {
  const { SetDayRoom } = DataProviderInstance();
  const current = useMemo(() => resolveRoomFor(assignments, resource), [assignments, resource]);
  const bands = useMemo(() => bandsFor(assignments, resource?.Id), [assignments, resource]);

  const [roomId, setRoomId] = useState(current.roomId);
  const [wholeDay, setWholeDay] = useState(true);
  const [startTime, setStartTime] = useState(() => suggestedTime(date));
  const [preview, setPreview] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const effectiveStart = wholeDay ? DAY_START : startTime;
  const roomLabelOf = (id) => rooms.find((r) => String(r.Id) === String(id))?.Label || '';
  const selectedLabel = roomLabelOf(roomId);

  // Prévia: quantos agendamentos mudam com a escolha atual (com um pequeno atraso
  // para não chamar a API a cada clique seguido)
  useEffect(() => {
    if (!resource?.Id || !date) return undefined;
    if (!wholeDay && !/^([01]\d|2[0-3]):[0-5]\d$/.test(startTime)) {
      setPreview(null);
      return undefined;
    }
    setIsPreviewing(true);
    const timer = setTimeout(() => {
      SetDayRoom({ resourceId: resource.Id, date, roomId, startTime: effectiveStart, preview: true }, (result) => {
        setIsPreviewing(false);
        if (result?.status === 'SUCCESS') {
          setPreview(result.data);
          setError('');
        } else {
          setPreview(null);
          setError(result?.error || 'Não foi possível calcular a prévia.');
        }
      });
    }, 250);
    return () => clearTimeout(timer);
  }, [resource?.Id, date, roomId, wholeDay, startTime, effectiveStart, SetDayRoom]);

  const handleApply = () => {
    if (isSaving) return;
    setIsSaving(true);
    setError('');
    SetDayRoom({ resourceId: resource.Id, date, roomId, startTime: effectiveStart, preview: false }, (result) => {
      setIsSaving(false);
      if (result?.status === 'SUCCESS') {
        if (onApplied) onApplied({ ...result.data, roomId, roomLabel: selectedLabel, startTime: effectiveStart });
        onClose();
      } else {
        setError(result?.error || 'Não foi possível aplicar a sala.');
      }
    });
  };

  const options = [{ id: '', label: 'Sem sala', hint: '' }].concat(
    rooms.map((room) => ({
      id: String(room.Id),
      label: room.Label,
      hint: [
        String(room.Id) === String(resource?.DefaultRoomId || '') ? 'padrão da agenda' : '',
        Number(room.Capacity || 1) > 1 ? `até ${room.Capacity} pessoas` : '',
      ].filter(Boolean).join(' · '),
    })),
  );

  const content = (
    <div className={styles.body}>
      <p className={styles.date}>{formatDate(date)}</p>

      {bands.length > 1 && (
        <p className={styles.bands}>
          Hoje: {bands.map((b, i) => `${b.roomId != null ? roomLabelOf(b.roomId) || 'sala' : 'sem sala'}${i === 0 ? '' : ` a partir das ${b.startTime}`}`).join(', ')}.
        </p>
      )}

      <div className={styles.options} role="radiogroup" aria-label="Sala">
        {options.map((option) => {
          const selected = option.id === roomId;
          return (
            <button
              key={option.id || 'none'}
              type="button"
              role="radio"
              aria-checked={selected}
              className={`${styles.option} ${selected ? styles.optionSelected : ''} ${option.id === '' ? styles.optionNone : ''}`}
              onClick={() => setRoomId(option.id)}
            >
              <span className={styles.optionCheck}>{selected && <CheckIcon sx={{ fontSize: 14 }} />}</span>
              <span className={styles.optionLabel}>{option.label}</span>
              {option.hint && <span className={styles.optionHint}>{option.hint}</span>}
            </button>
          );
        })}
      </div>

      <div className={styles.when}>
        <span className={styles.whenLabel}>Vale para</span>
        <div className={styles.whenToggle}>
          <button
            type="button"
            className={`${styles.whenButton} ${wholeDay ? styles.whenButtonActive : ''}`}
            onClick={() => setWholeDay(true)}
          >
            O dia todo
          </button>
          <button
            type="button"
            className={`${styles.whenButton} ${!wholeDay ? styles.whenButtonActive : ''}`}
            onClick={() => setWholeDay(false)}
          >
            A partir de
          </button>
          {!wholeDay && (
            <input
              type="time"
              className={styles.timeInput}
              value={startTime}
              step={300}
              onChange={(e) => setStartTime(e.target.value)}
              aria-label="A partir de"
            />
          )}
        </div>
      </div>

      <p className={`${styles.preview} ${isPreviewing ? styles.previewLoading : ''}`}>
        {preview ? previewSentence(preview, selectedLabel, effectiveStart) : (isPreviewing ? 'Calculando...' : '')}
      </p>
      {!wholeDay && (
        <p className={styles.hint}>Os agendamentos de antes desse horário continuam na sala em que estão.</p>
      )}
      {error && <p className={styles.error}>{error}</p>}
    </div>
  );

  const footer = (
    <div className={styles.footer}>
      <CustomButton style="primary" variant="outlined" label="Cancelar" onClick={onClose} enabled={!isSaving} />
      <CustomButton style="primary" variant="contained" label="Aplicar" onClick={handleApply} enabled={!isSaving && !!preview} loading={isSaving} />
    </div>
  );

  return (
    <PopupBase
      Title={`Sala de ${resource?.Label || 'agenda'}`}
      Content={content}
      Footer={footer}
      OnClose={onClose}
      maxWidth="xs"
    />
  );
}

export default DayRoomPopup;
